/**
 * Configuracion del negocio.
 *
 * ESTA PANTALLA MENTIA
 * --------------------
 * La version anterior guardaba la cotizacion en el estado local de
 * `MonedaProvider` y mostraba "Cotizacion actualizada". La base no se enteraba:
 * el dolar seguia como estaba y los precios no se recalculaban.
 *
 * Peor que no tener la pantalla, porque alguien podia cotizarle a un cliente con
 * un numero que el sistema no conocia.
 *
 * Ahora escribe de verdad, contra `PATCH /config`. Y eso tiene una consecuencia
 * que hay que decir en pantalla: guardar la cotizacion dispara
 * `trg_empresa_config_reprecia` en la base, que recalcula el precio de venta de
 * TODO el catalogo en la misma transaccion. No es un campo de display.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw, Settings, Store } from "lucide-react";

import {
  formatear,
  formatearCotizacion,
  formatearNumero,
  normalizarTelefonoAr,
  parsearImporte,
} from "@tora/shared";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { SeccionUsuarios } from "@/components/usuarios/SeccionUsuarios";
import { aNumero } from "@/lib/tipos";
import { useConfig, useActualizarConfig, useSincronizarCotizacion } from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";

export const Route = createFileRoute("/configuracion")({ component: Configuracion });

function Configuracion(): JSX.Element {
  const { data, isLoading } = useConfig();
  const guardar = useActualizarConfig();
  const sincronizar = useSincronizarCotizacion();

  const config = data?.config;
  const vigente = aNumero(data?.cotizacion_vigente ?? 0);
  // Cuantos productos se van a reprecificar. Es el numero que convierte "cambio
  // un campo" en "cambio el precio de N productos". Lo cuenta el backend con el
  // mismo criterio que el recalculo: antes era el total de productos activos
  // (329), y los que se mueven son los de costo en otra moneda sin precio a
  // mano (162).
  const cuantos = aNumero(data?.repreciables ?? 0);

  const [modo, setModo] = useState<"AUTO" | "MANUAL">("MANUAL");
  const [manual, setManual] = useState<number | null>(null);
  const [ajuste, setAjuste] = useState<number | null>(0);
  // El texto crudo del ajuste: se necesita para distinguir "vacío" de "no parsea".
  const [ajusteTexto, setAjusteTexto] = useState("0");
  const [negocio, setNegocio] = useState({
    nombre: "",
    whatsapp: "",
    instagram: "",
    direccion: "",
    localidad: "",
  });

  // El formulario se siembra con lo que hay en la base, no con defaults: un
  // default que pisa el valor real es como se pierde una configuracion.
  useEffect(() => {
    if (!config) return;
    setModo(config.cotizacion_modo === "AUTO" ? "AUTO" : "MANUAL");
    setManual(aNumero(config.cotizacion_manual));
    setAjuste(aNumero(config.ajuste_fijo));
    setAjusteTexto(String(aNumero(config.ajuste_fijo)).replace(".", ","));
    setNegocio({
      nombre: config.nombre ?? "",
      whatsapp: config.whatsapp ?? "",
      instagram: config.instagram ?? "",
      direccion: config.direccion ?? "",
      localidad: config.localidad ?? "",
    });
  }, [config]);

  // Cada modo mira lo suyo: en automatica el manual no cuenta, y en manual el
  // ajuste tampoco. Antes se comparaba todo: ir a «La cargo yo» (que siembra
  // el manual con el dolar de hoy) y volver a «Automática» sin tocar nada
  // dejaba el aviso «Esto mueve precios» con un dolar que no cambiaba.
  const cambioLaCotizacion =
    config != null &&
    (modo !== config.cotizacion_modo ||
      (modo === "MANUAL"
        ? manual !== aNumero(config.cotizacion_manual)
        : ajuste !== aNumero(config.ajuste_fijo)));

  const ajusteInvalido = ajusteTexto.trim() !== "" && ajuste === null;

  // El dolar que va a regir al guardar, para decirlo en el aviso. En
  // automatica solo se sabe si ya estaba en automatica (se mueve el ajuste
  // sobre el mismo blue); pasar de manual a automatica depende del blue que
  // traiga, y ese numero no lo tiene la pantalla.
  const nuevaCotizacion =
    modo === "MANUAL"
      ? manual !== null && manual > 0
        ? manual
        : null
      : config?.cotizacion_modo === "AUTO" && ajuste !== null
        ? vigente - aNumero(config.ajuste_fijo) + ajuste
        : null;
  const variacionPct =
    nuevaCotizacion !== null && vigente > 0 ? ((nuevaCotizacion - vigente) / vigente) * 100 : null;

  // El WhatsApp del catalogo. Se muestra como va a quedar el link: escrito
  // «0341 15 555-1234» armaba wa.me/5490341155551234, un numero que no existe.
  const whatsappEscrito = negocio.whatsapp.trim();
  const telefono = whatsappEscrito ? normalizarTelefonoAr(whatsappEscrito) : null;
  // Con menos de 10 digitos no es un celular argentino con ninguna lectura:
  // «123» se guardaba (solo salia un aviso gris) y los botones del catalogo
  // pasaban a abrir wa.me/549123. Eso frena el Guardar; con MAS de 10 queda
  // como aviso, porque puede ser un numero de otro pais bien escrito.
  const whatsappCorto = telefono !== null && telefono.e164.length - 3 < 10;
  const whatsappInvalido = whatsappEscrito !== "" && (telefono === null || whatsappCorto);

  if (isLoading) {
    return <Cargando />;
  }

  return (
    <Pagina ancho="lectura">
      <PageHeader titulo="Configuración" icono={Settings} />

      {/* ══ Cotización ══ */}
      <Card as="section" padding="none">
        <div className="border-b px-4 py-3">
          <TituloSeccion nivel="seccion">Cotización del dólar</TituloSeccion>
          <p className="text-sm text-muted-foreground">
            Hoy el sistema usa{" "}
            <span className="font-medium tabular-nums text-foreground">
              {formatearCotizacion(vigente)}
            </span>
            .
          </p>
        </div>

        <div className="space-y-4 p-4">
          {/* Traer el blue de Buenos Aires (DolarHoy, con DolarApi de respaldo) */}
          <Card
            variant="muted"
            padding="compacto"
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">Traerla automáticamente</div>
              <p className="text-xs text-muted-foreground">
                Consulta el dólar blue de Buenos Aires (DolarHoy) y lo deja como vigente. Si no
                responde, usa el blue nacional de DolarApi.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              cargando={sincronizar.isPending}
              onClick={() =>
                sincronizar.mutate(undefined, {
                  onSuccess: (r: any) =>
                    mostrarExito(
                      `Dólar actualizado: ${formatear(aNumero(r?.cotizacion_vigente), "ARS")}`,
                      `Se recalcularon los precios del catálogo.`,
                    ),
                  onError: mostrarError,
                })
              }
            >
              {/* Cargando, el boton ya pone su spinner en el lugar del icono. */}
              {sincronizar.isPending ? null : <RefreshCw aria-hidden="true" />}
              Actualizar ahora
            </Button>
          </Card>

          {/* Modo */}
          <div className="space-y-2" role="group" aria-labelledby="modo-cotizacion">
            <Label id="modo-cotizacion">De dónde sale la cotización</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              <Opcion
                activo={modo === "MANUAL"}
                onClick={() => {
                  // Viniendo de automatica, el campo arranca en el dolar de hoy.
                  // Antes arrancaba en el ultimo manual guardado (1.200 con el
                  // blue a 1.590), y dos clics repreciaban todo el catalogo
                  // con un dolar viejo.
                  if (modo === "AUTO" && vigente > 0) setManual(vigente);
                  setModo("MANUAL");
                }}
                titulo="La cargo yo"
                detalle="El sistema usa el número que pongas acá, sin importar lo que diga DolarHoy."
              />
              <Opcion
                activo={modo === "AUTO"}
                onClick={() => setModo("AUTO")}
                titulo="Automática"
                detalle="Usa la última cotización del blue de DolarHoy, más los pesos de ajuste de abajo."
              />
            </div>
          </div>

          {modo === "MANUAL" ? (
            <div className="max-w-xs">
              <InputImporte
                label="Cotización"
                valor={manual}
                moneda="ARS"
                onValorChange={setManual}
                ayuda="Cuántos pesos vale un dólar."
              />
            </div>
          ) : (
            <Campo
              label="Ajuste sobre la automática ($)"
              className="max-w-xs"
              error={
                ajusteInvalido ? "No se entiende el número. Probá con «20» o «1.500»." : undefined
              }
              ayuda="El local opera a un valor propio, distinto del publicado. Con 30, el sistema usa $ 30 más que el blue de DolarHoy."
            >
              {/* Sin 0 silencioso: lo que no parsea queda en null, se marca y no
                  se guarda. Antes "abc" se convertia en NaN y "" en 0. */}
              <Input
                id="ajuste"
                value={ajusteTexto}
                onChange={(e) => {
                  setAjusteTexto(e.target.value);
                  setAjuste(e.target.value.trim() === "" ? null : parsearImporte(e.target.value));
                }}
                inputMode="decimal"
                className="text-right tabular-nums"
              />
            </Campo>
          )}

          {/* La advertencia que hace honesta a esta pantalla. */}
          {cambioLaCotizacion && (
            <Aviso tono="warning" titulo="Esto mueve precios.">
              {nuevaCotizacion !== null && variacionPct !== null ? (
                <span className="block">
                  El dólar pasa de {formatear(vigente, "ARS")} a{" "}
                  <span className="font-medium">{formatear(nuevaCotizacion, "ARS")}</span> (
                  {variacionPct >= 0 ? "+" : "−"}
                  {formatearNumero(Math.abs(variacionPct), 1)} %).
                </span>
              ) : null}
              {/* El numero es el de `recalcular_precios`: costo en una moneda y
                  precio en la otra. Los de precio en dolares no se recalculan,
                  pero en pesos si cambian, y eso tambien hay que decirlo. */}
              Al guardar, el sistema recalcula el precio de venta de{" "}
              <span className="font-medium">
                {cuantos === 1 ? "1 producto activo" : `${cuantos} productos activos`}
              </span>{" "}
              que cuestan en una moneda y se venden en la otra. Los que tienen precio puesto a mano
              no se tocan, y los que tienen precio en dólares lo conservan: cambia lo que valen en
              pesos.
            </Aviso>
          )}
        </div>
      </Card>

      {/* ══ Negocio ══ */}
      <Card as="section" padding="none">
        <div className="border-b px-4 py-3">
          <TituloSeccion nivel="seccion" icono={Store}>
            Datos del negocio
          </TituloSeccion>
        </div>
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <Campo label="Nombre">
            <Input
              value={negocio.nombre}
              onChange={(e) => setNegocio((n) => ({ ...n, nombre: e.target.value }))}
            />
          </Campo>
          <Campo
            label="WhatsApp"
            error={
              whatsappCorto
                ? `Le faltan números: el catálogo lo abriría como ${telefono?.legible ?? ""}. Escribilo con característica: «341 555-1234».`
                : whatsappInvalido
                  ? "No es un teléfono. Escribilo con característica: «341 555-1234»."
                  : undefined
            }
            ayuda={
              telefono && !whatsappCorto
                ? telefono.confianza === "alta"
                  ? `El catálogo abre el chat con ${telefono.legible}.`
                  : `Revisalo: el catálogo lo abriría como ${telefono.legible}, y no parece un celular argentino.`
                : "Aparece en el catálogo público, con el mensaje ya escrito."
            }
          >
            <Input
              value={negocio.whatsapp}
              onChange={(e) => setNegocio((n) => ({ ...n, whatsapp: e.target.value }))}
            />
          </Campo>
          <Campo label="Instagram">
            <Input
              value={negocio.instagram}
              onChange={(e) => setNegocio((n) => ({ ...n, instagram: e.target.value }))}
            />
          </Campo>
          <Campo label="Localidad">
            <Input
              value={negocio.localidad}
              onChange={(e) => setNegocio((n) => ({ ...n, localidad: e.target.value }))}
            />
          </Campo>
          <Campo label="Dirección" className="sm:col-span-2">
            <Input
              value={negocio.direccion}
              onChange={(e) => setNegocio((n) => ({ ...n, direccion: e.target.value }))}
            />
          </Campo>
        </div>
      </Card>

      <div className="flex justify-end pb-4">
        <Button
          cargando={guardar.isPending}
          disabled={ajusteInvalido || whatsappInvalido}
          onClick={() =>
            guardar.mutate(
              {
                cotizacion_modo: modo,
                // Solo lo del modo elegido: el trigger reprecia con cualquier
                // cambio de estas columnas, y guardar en automatica el manual
                // sembrado repreciaba el catalogo sin que nada cambiara.
                ...(modo === "MANUAL" && manual !== null && manual > 0
                  ? { cotizacion_manual: manual }
                  : {}),
                ...(modo === "AUTO" && ajuste !== null ? { ajuste_fijo: ajuste } : {}),
                nombre: negocio.nombre,
                whatsapp: negocio.whatsapp,
                instagram: negocio.instagram,
                direccion: negocio.direccion,
                localidad: negocio.localidad,
              },
              {
                onSuccess: (r: any) =>
                  mostrarExito(
                    "Configuración guardada",
                    cambioLaCotizacion
                      ? `El sistema usa ${formatear(aNumero(r?.cotizacion_vigente), "ARS")} por dólar y ya recalculó los precios.`
                      : undefined,
                  ),
                onError: mostrarError,
              },
            )
          }
        >
          Guardar
        </Button>
      </div>

      {/* ══ Usuarios ══ Va despues del Guardar: tiene sus propios botones y no
          espera a ese. */}
      <SeccionUsuarios />
    </Pagina>
  );
}

/**
 * Tarjeta-radio: un `Button outline` grande con `aria-pressed`, como el
 * selector de formato de `DialogExportar`. El alto lo da el contenido
 * (`h-auto`, tambien en `sm:`), y el activo se marca con borde y fondo via el
 * atributo, sin duplicar clases en el feature.
 */
function Opcion({
  activo,
  onClick,
  titulo,
  detalle,
}: {
  activo: boolean;
  onClick: () => void;
  titulo: string;
  detalle: string;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      aria-pressed={activo}
      onClick={onClick}
      className="h-auto flex-col items-start gap-1 whitespace-normal px-4 py-3 text-left aria-pressed:border-foreground aria-pressed:bg-accent sm:h-auto"
    >
      <span className="text-sm font-medium text-foreground">{titulo}</span>
      <span className="text-xs font-normal text-muted-foreground">{detalle}</span>
    </Button>
  );
}
